import { useEffect, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import { HEADER_NAV } from '../../data/site';
import { SiteButton } from './SiteButton';
import { SiteBrand } from './SiteBrand';

function navClass(isActive: boolean) {
  const base = 'nav-link w-inline-block site-nav__link';
  return isActive ? `${base} w--current is-active` : base;
}

interface SiteNavMenuProps {
  id: string;
  className: string;
  menuOpen: boolean;
  pathname: string;
  variant: 'desktop' | 'mobile';
  onNavigate: () => void;
  useLightChrome: boolean;
}

function SiteNavMenu({
  id,
  className,
  menuOpen,
  pathname,
  variant,
  onNavigate,
  useLightChrome,
}: SiteNavMenuProps) {
  const isMobile = variant === 'mobile';

  return (
    <nav
      id={id}
      role="navigation"
      className={className}
      aria-label="Main navigation"
      aria-hidden={isMobile ? !menuOpen : undefined}
    >
      {isMobile ? (
        <div className="site-nav__menu-head">
          <p className="site-nav__menu-kicker">Menu</p>
        </div>
      ) : null}

      <ul role="list" className="nav-menu w-list-unstyled site-nav__list">
        {HEADER_NAV.map((item, index) => {
          const isActive = pathname === item.href;
          return (
            <li
              key={item.href}
              className="nav-item"
              style={isMobile ? ({ '--nav-index': index } as CSSProperties) : undefined}
            >
              <a
                href={item.href}
                className={navClass(isActive)}
                aria-current={isActive ? 'page' : undefined}
                onClick={onNavigate}
              >
                <div className="nav-link-text black site-nav__text">{item.label}</div>
                {isMobile ? (
                  <span className="site-nav__link-icon-wrap" aria-hidden="true">
                    <svg className="site-nav__link-icon" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8h10M9 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                ) : null}
                <div className="nav-link-underline black site-nav__underline" />
              </a>
            </li>
          );
        })}
      </ul>

      {isMobile ? (
        <div className="site-nav__mobile-cta">
          <SiteButton href="/waitlist" variant={useLightChrome ? 'light' : 'dark'}>
            Get Started
          </SiteButton>
        </div>
      ) : null}
    </nav>
  );
}

export function SiteNavBar() {
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('site-nav-open', menuOpen);
    return () => document.body.classList.remove('site-nav-open');
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const useLightChrome = false;

  return (
    <>
      <header
        className={[
          'site-header',
          isHome ? 'site-header--home' : '',
          scrolled ? 'site-header--scrolled' : 'site-header--top',
          menuOpen ? 'site-header--open' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div role="banner" className="navbar-container site-nav__container">
          <div className="navbar-wrapper site-nav__bar">
            <div className="navbar-left">
              <a
                href="/"
                aria-current={isHome ? 'page' : undefined}
                className="navbar-brand site-nav__brand"
                onClick={closeMenu}
              >
                <SiteBrand variant="nav" />
              </a>
            </div>

            <div className="navbar-center site-nav__center site-nav__center--desktop">
              <SiteNavMenu
                id="site-nav-menu-desktop"
                className="nav-menu-wrapper site-nav__menu site-nav__menu--desktop"
                menuOpen={menuOpen}
                pathname={pathname}
                variant="desktop"
                onNavigate={closeMenu}
                useLightChrome={useLightChrome}
              />
            </div>

            <div className="navbar-right site-nav__actions">
              <div className="nav-button-wrapper site-nav__cta">
                <SiteButton href="/waitlist" variant={useLightChrome ? 'light' : 'dark'}>
                  Get Started
                </SiteButton>
              </div>

              <button
                type="button"
                className={`site-nav__toggle${menuOpen ? ' is-open' : ''}`}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="site-nav-menu-mobile"
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span className="site-nav__toggle-line" />
                <span className="site-nav__toggle-line" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className={`site-nav__mobile-shell${menuOpen ? ' is-open' : ''}`}>
        <SiteNavMenu
          id="site-nav-menu-mobile"
          className={`site-nav__menu site-nav__menu--mobile${menuOpen ? ' is-open' : ''}`}
          menuOpen={menuOpen}
          pathname={pathname}
          variant="mobile"
          onNavigate={closeMenu}
          useLightChrome={useLightChrome}
        />
      </div>

      <button
        type="button"
        className={`site-nav__backdrop${menuOpen ? ' is-visible' : ''}`}
        aria-label="Close menu"
        tabIndex={menuOpen ? 0 : -1}
        onClick={closeMenu}
      />
    </>
  );
}
